import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import clsx from 'clsx';
import { validate } from 'uuid';

import { useInjection } from '@context/inversify-context-provider';
import { Utils } from '@helpers/utils';
import { mutateIntegrationState, useGetOnboardingsWithRelationsMultiple, useGetUser } from '@hooks/apiHooks';
import { useDecodedParams } from '@hooks/useDecodedParams';
import { TYPES } from '@libs/InversifyType';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import { RootState } from '@store/rootReducer';
import { snackbarSlice } from '@store/slices/snackbar';

import { AamBackendApi } from '../../../../../libs/aamBackendApi';

import styles from './CandidateInfo.module.scss';
interface IntegrationStateModalProps {
  integrationState: any | null;
  isActive: boolean;
  closeModal: () => void;
}

const excludeIntegrationKeysArray = [
  'GDSPCreateCandidateCommand',
  'GDSPUpdateCandidateCommand',
  'cronJobIntegrationErrorStatus',
];

const IntegrationStateEditModal: React.FC<IntegrationStateModalProps> = ({
  integrationState,
  isActive,
  closeModal,
}) => {
  const [commandInfo, setCommandInfo] = useState<any>(null);
  const [editObject, setEditObject] = useState<{ name: string; object: any } | null>(null);
  const [newIntegrationState, setNewIntegrationState] = useState(integrationState);
  const [retriggeringInProgress, setRetriggeringInProgress] = useState<boolean>(false);

  const { clientOrganizationId, userId, applicationId, configurationId } = useDecodedParams();
  const { tenantId } = useSelector((state: RootState) => state.tenant);
  const { t } = useTranslation(['recruiter', 'integration-state-faq']);
  const aamBackendApi = useInjection<AamBackendApi>(TYPES.AamBackendApi);
  const dispatch = useDispatch();

  const { data: user } = useGetUser(tenantId, userId, { refetchOnMount: false });
  const { data: onboarding } = useGetOnboardingsWithRelationsMultiple(
    clientOrganizationId,
    configurationId,
    userId,
    applicationId,
  );

  const { mutate: updateIntegrationState, isPending: loading } = mutateIntegrationState(
    clientOrganizationId,
    configurationId,
    userId,
    applicationId,
    (response: any) => {
      setNewIntegrationState(response);
      setEditObject(null);
      dispatch(
        snackbarSlice.actions.showSuccess({
          message: `Integration state updated successfully`,
        }),
      );
    },
  );

  const retriggerIntegration = async () => {
    if (!user || !onboarding || !onboarding?.applicationId || !user.id) return;
    try {
      setRetriggeringInProgress(true);
      await aamBackendApi.retriggerIntegration(
        onboarding.clientOrganizationId,
        onboarding.configurationId,
        user.id,
        onboarding.applicationId,
      );
      dispatch(
        snackbarSlice.actions.showSuccess({
          message: t('recruiter:GENERAL.CANDIDATE_DETAILS.retriggerIntegrationSuccess'),
        }),
      );
    } catch (err) {
      setRetriggeringInProgress(false);
      Utils.consoleLog(err.message);
      dispatch(
        snackbarSlice.actions.showError({
          message: t('recruiter:GENERAL.CANDIDATE_DETAILS.retriggerIntegrationError'),
        }),
      );
    }
  };

  const getIntegrationCommandInfo = async (commandId: string) => {
    const integrationCommandInfo = await aamBackendApi.getIntegrationCommandInfo(commandId);
    if (integrationCommandInfo) {
      setCommandInfo(integrationCommandInfo);
    }
  };
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const updatedIntegrationState = { ...newIntegrationState };
    updatedIntegrationState[name] = value;
    setNewIntegrationState(updatedIntegrationState);
  };

  const handleObjectChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    let parsedValue = value;
    try {
      parsedValue = JSON.parse(value);
    } catch (err) {
      Utils.consoleLog('Invalid JSON');
      return;
    }
    setEditObject({ name: name, object: parsedValue });
  };

  const saveCommandObjectChanges = async () => {
    if (!editObject) return;
    const { name, object } = editObject;
    if (name === 'integrationState') {
      setNewIntegrationState(object);
    } else {
      const updatedIntegrationState = { ...newIntegrationState };
      updatedIntegrationState[name] = JSON.stringify(object);
      setNewIntegrationState(updatedIntegrationState);
    }
    setEditObject(null);
  };

  const handleRemoveProperty = (key: string) => {
    const updatedIntegrationState = { ...newIntegrationState };
    updatedIntegrationState[key] = 'remove';
    setNewIntegrationState(updatedIntegrationState);
  };

  const handleSubmitState = async () => {
    await updateIntegrationState(newIntegrationState);
  };
  return (
    <Dialog open={isActive} onClose={closeModal}>
      <Box>
        <DialogTitle>
          <Box display="flex" flexWrap="wrap" width="100%">
            <p className="tag-ds  h4">{t('recruiter:GENERAL.CANDIDATE_DETAILS.integrationState')}</p>
            <div className="tag-ds" style={{ marginLeft: 'auto' }}>
              <button onClick={() => setEditObject({ name: 'integrationState', object: newIntegrationState })}>
                <span className="material-icons">settings</span>
              </button>
            </div>
          </Box>
        </DialogTitle>
        <DialogContent>
          {Object.keys(newIntegrationState || {})
            .filter((k) => newIntegrationState[k] !== 'remove')
            .map((key, index) => {
              let integrationStateProp = newIntegrationState?.[key];
              try {
                integrationStateProp = JSON.parse(integrationStateProp);
              } catch (err) {
                // nothing
              }
              const isClickable = validate(integrationStateProp?.toString() || '');
              const isObject = typeof integrationStateProp === 'object' && integrationStateProp !== null;
              return (
                <Box display="flex" flexWrap="wrap" key={index} mb={1} width="100%" flexDirection="column">
                  {isObject ? (
                    <div className={clsx('tag-ds')}>
                      <label>"{key}"</label>
                      <div className={styles.integrationState__buttons}>
                        <button onClick={() => setEditObject({ name: key, object: integrationStateProp })}>Edit</button>
                        <button onClick={() => handleRemoveProperty(key)}>
                          <span className="material-icons">delete</span>
                          Remove
                        </button>
                      </div>
                    </div>
                  ) : (
                    <Box key={index} width="100%" display="flex" flexDirection="column" mb={1}>
                      <div className="tag-ds input-wrapper" style={{ marginBottom: '10px' }}>
                        <label>"{key}"</label>
                        <input
                          value={integrationStateProp?.toString()}
                          type="text"
                          name={key}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div
                        className={clsx('tag-ds', styles.integrationState__buttons)}
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'flex-start',
                          justifyContent: 'flex-start',
                          width: '100%',
                          gap: '10px',
                          marginRight: 'auto',
                        }}
                      >
                        {isClickable && (
                          <button onClick={() => getIntegrationCommandInfo(integrationStateProp)}>
                            {t('recruiter:GENERAL.CANDIDATE_DETAILS.Details')}
                          </button>
                        )}
                        <button onClick={() => handleRemoveProperty(key)}>
                          <span className="material-icons">delete</span>
                          Remove
                        </button>
                      </div>
                    </Box>
                  )}
                </Box>
              );
            })}
        </DialogContent>
        <DialogActions>
          <div className="tag-ds ">
            <button
              disabled={
                retriggeringInProgress ||
                !Object.keys(newIntegrationState || {}).some(
                  (key) => newIntegrationState[key] === 'error' && !excludeIntegrationKeysArray.includes(key),
                )
              }
              onClick={retriggerIntegration}
            >
              {t('recruiter:GENERAL.CANDIDATE_DETAILS.retriggerIntegration')}
            </button>
          </div>
          <div className="tag-ds ">
            <button onClick={handleSubmitState} disabled={loading}>
              Save Changes
            </button>
          </div>
        </DialogActions>
        {commandInfo && (
          <Dialog open={!!commandInfo} onClose={() => setCommandInfo(null)}>
            <DialogTitle>
              <Box>
                <Typography variant="h5">{commandInfo?.id}</Typography>
                <Box onClick={() => setCommandInfo(null)}>
                  <CloseIcon />
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent>
              {commandInfo &&
                commandInfo.map((data: any, index: number) => (
                  <Box my={1} key={index}>
                    <Typography variant="h5">{data.topic || ''}</Typography>
                    <pre style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(data, null, 2)}</pre>
                  </Box>
                ))}
            </DialogContent>
          </Dialog>
        )}
        {editObject && (
          <Dialog open={!!editObject} onClose={() => setEditObject(null)}>
            <DialogTitle>
              <Box>
                <Box onClick={() => setEditObject(null)}>
                  <CloseIcon />
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent>
              {editObject && (
                <Box my={1}>
                  <Typography variant="h5">{editObject.name || ''}</Typography>
                  <textarea
                    className="integration-state-prop--active"
                    value={JSON.stringify(editObject.object, null, 2)}
                    name={editObject.name}
                    onChange={handleObjectChange}
                    rows={40}
                    cols={100}
                    style={{ width: '100%' }}
                  />
                  <Typography variant="caption">
                    This input field only accepts valid JSON, please edit the JSON in an external editor and paste it
                    here.
                    <br />
                    <span style={{ color: 'red' }}>
                      Please be careful when editing the integration state, as it can affect the integration process.
                    </span>
                  </Typography>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <div className="tag-ds ">
                <button onClick={saveCommandObjectChanges}>Save Changes</button>
              </div>
            </DialogActions>
          </Dialog>
        )}
      </Box>
    </Dialog>
  );
};

export default IntegrationStateEditModal;
